import { useEffect, useState } from "react";
import api from "@/lib/api";

// Convertitori ISO UTC <-> input datetime-local (Europe/Rome).
// (riusa la stessa logica di ExtraImpostazioniView)
function isoUtcToRomeLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Rome",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(d);
    const m = Object.fromEntries(parts.map(p => [p.type, p.value]));
    return `${m.year}-${m.month}-${m.day}T${m.hour}:${m.minute}`;
  } catch { return ""; }
}

function romeLocalInputToIsoUtc(local) {
  if (!local) return null;
  try {
    const [date, time] = local.split("T");
    const [y, mo, d] = date.split("-").map(Number);
    const [hh, mm] = time.split(":").map(Number);
    // Calcola offset di Roma per quella data
    const probe = new Date(Date.UTC(y, mo - 1, d, hh, mm));
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Rome",
      hour: "2-digit", hour12: false,
    }).formatToParts(probe);
    const romeHour = parseInt(parts.find(p => p.type === "hour").value, 10);
    const offsetH = hh - romeHour;
    const dt = new Date(Date.UTC(y, mo - 1, d, hh + offsetH, mm));
    return dt.toISOString();
  } catch { return null; }
}

function fmtRomaUmana(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      timeZone: "Europe/Rome",
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }) + " (ora italiana)";
  } catch { return "—"; }
}

export default function SpotImpostazioniView() {
  const [cfg, setCfg] = useState(null);
  const [stato, setStato] = useState("chiuso");
  const [openLocal, setOpenLocal] = useState("");
  const [annoIscr, setAnnoIscr] = useState("");
  const [nota, setNota] = useState("");
  const [mailRicSubj, setMailRicSubj] = useState("");
  const [mailRicBody, setMailRicBody] = useState("");
  const [mailAccSubj, setMailAccSubj] = useState("");
  const [mailAccBody, setMailAccBody] = useState("");
  const [mailRifSubj, setMailRifSubj] = useState("");
  const [mailRifBody, setMailRifBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const c = await api.get("/mensa-matrice/spot-config");
      setCfg(c.data);
      setStato(c.data?.stato || "chiuso");
      setOpenLocal(isoUtcToRomeLocalInput(c.data?.open_at));
      setAnnoIscr(c.data?.anno_iscrizioni ?? "");
      setNota(c.data?.nota_alunno ?? "");
      setMailRicSubj(c.data?.mail_ricevuta_subject ?? "");
      setMailRicBody(c.data?.mail_ricevuta_body ?? "");
      setMailAccSubj(c.data?.mail_accettata_subject ?? "");
      setMailAccBody(c.data?.mail_accettata_body ?? "");
      setMailRifSubj(c.data?.mail_rifiutata_subject ?? "");
      setMailRifBody(c.data?.mail_rifiutata_body ?? "");
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore caricamento configurazione" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (stato === "programmato" && !openLocal) {
      setMsg({ ok: false, text: "Imposta una data/ora di apertura quando lo stato è 'Programmato'" });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      const payload = {
        stato,
        open_at: stato === "programmato" ? romeLocalInputToIsoUtc(openLocal) : null,
        anno_iscrizioni: annoIscr ? parseInt(annoIscr, 10) : null,
        nota_alunno: nota,
        mail_ricevuta_subject: mailRicSubj,
        mail_ricevuta_body: mailRicBody,
        mail_accettata_subject: mailAccSubj,
        mail_accettata_body: mailAccBody,
        mail_rifiutata_subject: mailRifSubj,
        mail_rifiutata_body: mailRifBody,
      };
      await api.put("/mensa-matrice/spot-config", payload);
      setMsg({ ok: true, text: "Configurazione salvata" });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore salvataggio" });
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 20, color: "#666" }}>Caricamento…</div>;

  const isOpenNow = !!cfg?.is_open_now;
  const statoLabel = stato === "chiuso" ? "CHIUSO" : (stato === "programmato" ? "PROGRAMMATO" : "APERTO");

  return (
    <div data-testid="spot-impostazioni" style={{ padding: "16px 20px", maxWidth: 900 }}>
      {/* Stato apertura */}
      <div style={{ padding: "14px 18px", background: isOpenNow ? "linear-gradient(90deg,#00695c,#004d40)" : "linear-gradient(90deg,#c62828,#8e0000)", border: `3px solid ${isOpenNow ? "#004d40" : "#8e0000"}`, borderRadius: 6, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", boxShadow: "0 4px 12px rgba(0,0,0,0.25)", marginBottom: 18 }}>
        <div style={{ fontSize: 30 }}>{isOpenNow ? "🟢" : "🔴"}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: "white", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Richieste Mensa SPOT {statoLabel}
          </div>
          <div style={{ fontSize: 11, color: "#fff", lineHeight: 1.5 }}>
            {cfg?.stato === "programmato" && cfg?.open_at
              ? <>Apertura programmata: <b>{fmtRomaUmana(cfg.open_at)}</b></>
              : cfg?.stato === "aperto"
                ? <>Portale: <b>aperto</b> (gli alunni possono richiedere subito)</>
                : <>Portale: <b>chiuso</b> (la card SPOT non è visibile)</>
            }
            {cfg?.updated_by && <><br/><span style={{ opacity: 0.85 }}>Ultimo cambio: {cfg.updated_by} · {fmtRomaUmana(cfg.updated_at)}</span></>}
          </div>
        </div>
      </div>

      {msg && (
        <div style={{ padding: "8px 12px", background: msg.ok ? "#e8f5e9" : "#ffebee", border: `1px solid ${msg.ok ? "#81c784" : "#ef9a9a"}`, color: msg.ok ? "#1b5e20" : "#c62828", fontSize: 12, marginBottom: 12, borderRadius: 3 }}>
          {msg.text}
        </div>
      )}

      <div style={{ background: "white", border: "1px solid #cfd8dc", borderRadius: 6, padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: "#263238", marginBottom: 12, paddingBottom: 8, borderBottom: "2px solid #cfd8dc", textTransform: "uppercase", letterSpacing: 0.5 }}>
          🍽️ Stato gestione &laquo;Richieste Mensa SPOT&raquo;
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {[
            { v: "chiuso", lbl: "🔴 Chiuso (senza data)", desc: "Card SPOT non visibile in /alunni." },
            { v: "programmato", lbl: "🟡 Programmato", desc: "Apre alla data sotto. Prima vedono 'Aprirà il…'." },
            { v: "aperto", lbl: "🟢 Aperto subito", desc: "Richieste attive immediatamente." },
          ].map(o => (
            <button key={o.v} type="button" onClick={() => setStato(o.v)}
              data-testid={`spot-stato-${o.v}`}
              style={{
                flex: "1 1 220px", padding: "12px 14px",
                border: `2px solid ${stato === o.v ? "#00695c" : "#cfd8dc"}`,
                background: stato === o.v ? "#e0f2f1" : "white",
                color: stato === o.v ? "#004d40" : "#37474f",
                fontWeight: 700, fontSize: 13, textAlign: "left", cursor: "pointer", borderRadius: 4,
              }}>
              <div>{o.lbl}</div>
              <div style={{ fontSize: 10, fontWeight: 500, marginTop: 4, color: "#666" }}>{o.desc}</div>
            </button>
          ))}
        </div>
        {stato === "programmato" && (
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#37474f", marginBottom: 4, letterSpacing: 0.3 }}>
                DATA E ORA APERTURA (Europe/Rome)
              </div>
              <input
                type="datetime-local"
                data-testid="spot-open-at"
                value={openLocal}
                onChange={e => setOpenLocal(e.target.value)}
                style={{ padding: "8px 10px", border: "2px solid #00695c", fontSize: 13, fontFamily: "Consolas, monospace" }}
              />
            </div>
            <div style={{ fontSize: 11, color: "#666", flex: "1 1 200px", lineHeight: 1.5 }}>
              Gli alunni vedranno la card SPOT come <b>chiusa</b> con indicata la data di apertura. Si aprirà <b>automaticamente</b> alla data/ora specificata.
            </div>
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving}
        data-testid="spot-save"
        style={{ padding: "10px 22px", background: "#00695c", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: saving ? "wait" : "pointer", borderRadius: 3, boxShadow: "0 2px 6px rgba(0,105,92,0.3)", marginBottom: 18 }}>
        {saving ? "Salvataggio…" : "💾 Salva configurazione"}
      </button>

      {/* NOTA ALUNNO */}
      <div style={{ background: "white", border: "1px solid #cfd8dc", borderRadius: 6, padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: "#263238", marginBottom: 10, paddingBottom: 6, borderBottom: "2px solid #cfd8dc", textTransform: "uppercase", letterSpacing: 0.5 }}>
          📝 Nota mostrata all'alunno
        </div>
        <textarea value={nota} onChange={e => setNota(e.target.value)} rows={6}
          data-testid="spot-nota-alunno"
          style={{ width: "100%", padding: 10, fontFamily: "inherit", fontSize: 12, border: "1px solid #cfd8dc", borderRadius: 3 }} />
        <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>
          Questo testo viene mostrato sotto la matrice di selezione giorno mensa SPOT.
        </div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
          <b>Anno scolastico di riferimento</b>: <input type="number" value={annoIscr} onChange={e => setAnnoIscr(e.target.value)}
            data-testid="spot-anno-iscr" style={{ width: 80, padding: "4px 6px", border: "1px solid #ccc", marginLeft: 6 }} placeholder="2025" />
          <span style={{ color: "#888", marginLeft: 6 }}>(lascia vuoto per usare l'anno della scheda alunno)</span>
        </div>
      </div>

      {/* MAIL TEMPLATES */}
      <div style={{ background: "white", border: "1px solid #cfd8dc", borderRadius: 6, padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: "#263238", marginBottom: 10, paddingBottom: 6, borderBottom: "2px solid #cfd8dc", textTransform: "uppercase", letterSpacing: 0.5 }}>
          ✉️ Modelli email
        </div>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>
          Segnaposto disponibili: <code>{"{nome}"}</code> <code>{"{cognome}"}</code> <code>{"{classe}"}</code> <code>{"{settore}"}</code> <code>{"{giorno_settimana}"}</code> <code>{"{anno_scol}"}</code> <code>{"{motivo}"}</code>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 700, color: "#ef6c00", fontSize: 12, marginBottom: 6 }}>📩 RICEVUTA (presa in carico, richiesta in sospeso)</div>
          <input value={mailRicSubj} onChange={e => setMailRicSubj(e.target.value)}
            data-testid="spot-mail-ric-subj" placeholder="Oggetto"
            style={{ width: "100%", padding: 8, fontSize: 12, border: "1px solid #ffcc80", marginBottom: 6, borderRadius: 3 }} />
          <textarea value={mailRicBody} onChange={e => setMailRicBody(e.target.value)} rows={7}
            data-testid="spot-mail-ric-body" placeholder="Corpo email"
            style={{ width: "100%", padding: 10, fontFamily: "inherit", fontSize: 12, border: "1px solid #ffcc80", borderRadius: 3 }} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 700, color: "#1b5e20", fontSize: 12, marginBottom: 6 }}>✅ ACCETTAZIONE</div>
          <input value={mailAccSubj} onChange={e => setMailAccSubj(e.target.value)}
            data-testid="spot-mail-acc-subj" placeholder="Oggetto"
            style={{ width: "100%", padding: 8, fontSize: 12, border: "1px solid #a5d6a7", marginBottom: 6, borderRadius: 3 }} />
          <textarea value={mailAccBody} onChange={e => setMailAccBody(e.target.value)} rows={7}
            data-testid="spot-mail-acc-body" placeholder="Corpo email"
            style={{ width: "100%", padding: 10, fontFamily: "inherit", fontSize: 12, border: "1px solid #a5d6a7", borderRadius: 3 }} />
        </div>

        <div>
          <div style={{ fontWeight: 700, color: "#c62828", fontSize: 12, marginBottom: 6 }}>❌ RIFIUTO</div>
          <input value={mailRifSubj} onChange={e => setMailRifSubj(e.target.value)}
            data-testid="spot-mail-rif-subj" placeholder="Oggetto"
            style={{ width: "100%", padding: 8, fontSize: 12, border: "1px solid #ef9a9a", marginBottom: 6, borderRadius: 3 }} />
          <textarea value={mailRifBody} onChange={e => setMailRifBody(e.target.value)} rows={7}
            data-testid="spot-mail-rif-body" placeholder="Corpo email"
            style={{ width: "100%", padding: 10, fontFamily: "inherit", fontSize: 12, border: "1px solid #ef9a9a", borderRadius: 3 }} />
        </div>
      </div>

      <button onClick={save} disabled={saving}
        data-testid="spot-save-bottom"
        style={{ padding: "10px 22px", background: "#00695c", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: saving ? "wait" : "pointer", borderRadius: 3, boxShadow: "0 2px 6px rgba(0,105,92,0.3)" }}>
        {saving ? "Salvataggio…" : "💾 Salva configurazione"}
      </button>
    </div>
  );
}
